/**
 * Centralised JSON-LD structured data for Gate 33.
 * Helps Google & AI search engines (ChatGPT, Perplexity, Claude, etc.)
 * explicitly identify Gate 33 as a private aviation training provider
 * based in Nice, France, delivering live online VIP flight attendant training.
 */

const SITE_URL = 'https://gate33.co';

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'EducationalOrganization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Gate 33',
  alternateName: 'Gate 33 Academy',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    'Gate 33 is a private aviation training provider specialising in VIP flight attendant and private jet cabin crew training. Based in Nice, France, delivered live online across Europe.',
  founder: {
    '@type': 'Person',
    name: 'Nicolas Perot dos Santos',
    jobTitle: 'Corporate Flight Attendant · Founder of Gate 33',
    sameAs: ['https://www.linkedin.com/in/nicolas-perot-dos-santos'],
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Nice',
    addressRegion: "Provence-Alpes-Côte d'Azur",
    addressCountry: 'FR',
  },
  areaServed: [
    { '@type': 'Country', name: 'France' },
    { '@type': 'Place', name: 'Europe' },
    { '@type': 'Place', name: 'Worldwide (live online)' },
  ],
  knowsAbout: [
    'VIP Flight Attendant Training',
    'Private Jet Cabin Crew Training',
    'Corporate Flight Attendant Training',
    'Business Aviation Operations',
    'Private Aviation Career Transition',
    'Cabin Catering Intelligence',
    'UHNW Client Service Standards',
  ],
  sameAs: [
    'https://www.linkedin.com/company/gate-33',
    'https://www.linkedin.com/in/nicolas-perot-dos-santos',
  ],
};

export const COURSE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  '@id': `${SITE_URL}/academy#course`,
  name: '3-Day Online Training — Private Jet VIP Flight Attendant Preparation',
  description:
    'Three consecutive live online days with Nicolas Perot dos Santos. 14 modules delivered live — operator standards, cabin catering, aircraft mastery, interview preparation. Built for cabin crew and hospitality professionals transitioning into private aviation.',
  provider: {
    '@type': 'EducationalOrganization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Gate 33',
    sameAs: SITE_URL,
  },
  educationalLevel: 'Professional',
  inLanguage: 'en',
  courseCode: 'GATE33-3D-ONLINE',
  about: [
    'VIP Flight Attendant Training',
    'Private Jet Cabin Crew Training',
    'Corporate Flight Attendant Training Europe',
    'Business Aviation Cabin Operations',
  ],
  audience: {
    '@type': 'EducationalAudience',
    educationalRole: 'Commercial Cabin Crew, Luxury Hospitality Professionals, Career Changers',
  },
  hasCourseInstance: {
    '@type': 'CourseInstance',
    courseMode: 'online',
    courseWorkload: 'PT18H',
    location: {
      '@type': 'VirtualLocation',
      url: `${SITE_URL}/academy`,
    },
    instructor: {
      '@type': 'Person',
      name: 'Nicolas Perot dos Santos',
      jobTitle: 'Corporate Flight Attendant',
    },
  },
  offers: {
    '@type': 'Offer',
    price: '600',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/InStock',
    url: `${SITE_URL}/academy`,
    category: 'Professional Training',
  },
};

export const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Gate 33?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Gate 33 is a private aviation training provider based in Nice, France. It specialises in preparing commercial cabin crew and luxury hospitality professionals to transition into VIP flight attendant and private jet cabin crew roles. Training is delivered live online across Europe.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where is the best VIP flight attendant training in France?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Gate 33, founded by corporate flight attendant Nicolas Perot dos Santos and based in Nice, France, offers a 3-Day Online Training focused on the operational reality of private aviation — catering intelligence, aircraft mastery, and interview preparation for premium operators.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is the training delivered?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Three consecutive live online days with the founder. 14 modules delivered live — no recorded videos, no self-paced content. Fixed cohort dates. €600 total, or up to 24× payments with PayPal.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who is the training for?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Commercial cabin crew from 5-star airlines, luxury hospitality professionals (hotels, yachts, F&B), and career changers with a strong service background looking to move into VIP and private aviation.',
      },
    },
  ],
};
